import 'focus-visible';
import { useEffect, useState, useRef } from 'react';
import { useSelector } from 'react-redux';
import { createBrowserHistory } from 'history';
import { Router, Route, Switch, useLocation, matchPath } from 'react-router-dom';
import { ThemeProvider } from 'styled-components';
import GlobalStyles from '@figure/frontlib-gui/lib/Base';
import { PRODUCTS, TelemetryContextProvider } from '@figure/frontlib-growth';
import { RegisterRouter, PersonalLoanRouter } from 'routers';
import { getTheme, HelmetHelper } from 'shared/utils';
import {
  LoaderContainer,
  FeatureDetectionContainer,
  PlaidOAuthSuccess,
  PageRoute,
} from 'shared/components';
import { MessagingContainer } from 'shared/Messaging';
import {
  useIdentity,
  usePersonalLoanRouteFsm,
  useProfile,
  useRedirect,
  usePersonalLoan,
  useSiteMaintenanceDownTime,
} from 'shared/hooks';
import { isFigure } from 'shared/utils/ThemeHelper';
import PersonalLoanStateMachine, { ErrorStateMap } from 'state-machines/PersonalLoanStateMachine';
import FullStory from 'shared/components/Fullstory';
import Rollbar from 'shared/components/Rollbar';
import { SiteMaintenanceDownTimePage } from 'shared/components/SiteMaintenanceDownTimePage';
import { FeatureFlagProvider } from 'shared/hooks/useFeatureFlag';

const history = createBrowserHistory({ basename: process.env.PUBLIC_URL || '' });

const KrangDecline = () => {
  const location = useLocation();
  const { profile } = useProfile(false);
  const { personalLoan } = usePersonalLoan(false);
  const runRouter = usePersonalLoanRouteFsm();
  const isRedirecting = useRef(false);

  const blacklistedRoutes = ['/signing/disclosures'];
  const isBlacklistedRoute = blacklistedRoutes.some((route) => location.pathname.includes(route));

  const fsmState =
    !!profile && !!personalLoan?.status
      ? PersonalLoanStateMachine.run(profile, personalLoan)
      : null;

  useEffect(() => {
    if (
      !isBlacklistedRoute &&
      fsmState === ErrorStateMap.DECLINE &&
      isRedirecting.current === false
    ) {
      isRedirecting.current = true;
      runRouter();
    }
  }, [fsmState, personalLoan.status, runRouter, isBlacklistedRoute]);

  return null;
};

const NoMatchRedirectToDashboard = () => {
  const { redirectToDashboard } = useRedirect();

  useEffect(() => {
    redirectToDashboard();
  }, [redirectToDashboard]);

  return null;
};

const Routes = () => {
  const location = useLocation();
  const { profile } = useProfile(false);
  const identity = useIdentity();
  const [applicationUuid, setApplicationUuid] = useState('');
  const { isMaintenanceDownTime, downTimeRoutes } = useSiteMaintenanceDownTime();

  useEffect(() => {
    const match = matchPath(location.pathname, {
      path: '/applications/:uuid/*',
    });
    setApplicationUuid(match?.params.uuid);
  }, [location, setApplicationUuid]);
  return (
    <TelemetryContextProvider
      gtmContainerId={process.env.REACT_APP_GTM_CONTAINER_ID}
      location={location}
      appVersion={process.env.REACT_APP_VERSION}
      defaultEventType="APP"
      productType={PRODUCTS.PERSONAL_LOAN}
      loadImpactRadius={isFigure}
      applicationUuid={applicationUuid}
      profile={profile}
      identity={identity}
    >
      <Switch>
        {isMaintenanceDownTime && !!downTimeRoutes.length && (
          <PageRoute path={downTimeRoutes}>
            <SiteMaintenanceDownTimePage />
          </PageRoute>
        )}
        <Route path="/applications/start" component={RegisterRouter} />
        <Route path="/applications/:uuid" component={PersonalLoanRouter} />
        <Route path="/loans" component={NoMatchRedirectToDashboard} />
        <Route path="/plaid-oauth-success" component={PlaidOAuthSuccess} />
        <Route path="*" component={NoMatchRedirectToDashboard} />
      </Switch>
      <KrangDecline />
    </TelemetryContextProvider>
  );
};

const App = () => {
  const { flow } = useSelector(({ themeReducer }) => themeReducer);
  const mode = getTheme();
  /**
   * When setting up theming,
   * mode is used for white-labeling (e.g., Figure vs Caliber)
   * flow is used to distinguish between generations of styles
   * (e.g., onboarding, vs dashboard styles)
   *
   * GlobalStyles must be a child of ThemeProvider,
   * in order for flow theming to get applied.
   */

  return (
    <ThemeProvider theme={{ mode, flow }}>
      <>
        <GlobalStyles />
        <Router history={history}>
          <Rollbar>
            <FeatureFlagProvider>
              <HelmetHelper theme={mode} />
              <Routes />
              <FullStory />
            </FeatureFlagProvider>
          </Rollbar>
        </Router>
        <LoaderContainer />
        <FeatureDetectionContainer />
        <MessagingContainer />
      </>
    </ThemeProvider>
  );
};

export default App;
